import type { GpxRoute, GpxTrackPoint } from "./types";

// ── Haversine distance in metres ──────────────────────────────────────────────

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Parse a .gpx file text string ────────────────────────────────────────────

export function parseGpx(text: string): GpxRoute {
  const doc = new DOMParser().parseFromString(text, "application/xml");

  const parseErr = doc.querySelector("parsererror");
  if (parseErr) throw new Error("Invalid GPX file: XML parse error.");

  // Collect all track points across all tracks and segments
  const trkptEls = Array.from(doc.querySelectorAll("trkseg trkpt"));

  // Fallback: route points (rte/rtept) if no track points
  const pts = trkptEls.length > 0
    ? trkptEls
    : Array.from(doc.querySelectorAll("rtept"));

  if (pts.length === 0) {
    throw new Error("No track points found in GPX file.");
  }
  if (pts.length === 1) {
    throw new Error("GPX file contains only one point — need at least 2.");
  }

  // Extract track name
  const nameEl = doc.querySelector("trk > name") ?? doc.querySelector("rte > name") ?? doc.querySelector("name");
  const name = nameEl?.textContent?.trim() || "Imported Route";

  // Build GpxTrackPoint array
  const trkpts: GpxTrackPoint[] = [];
  let distFromStart = 0;
  let elevationGainM = 0;
  let elevationLossM = 0;

  for (let i = 0; i < pts.length; i++) {
    const el = pts[i];
    const lat = parseFloat(el.getAttribute("lat") ?? "");
    const lng = parseFloat(el.getAttribute("lon") ?? "");
    if (isNaN(lat) || isNaN(lng)) continue;

    const eleText = el.querySelector("ele")?.textContent?.trim();
    const ele = eleText != null && eleText !== "" ? parseFloat(eleText) : null;
    const timeText = el.querySelector("time")?.textContent?.trim() ?? null;

    if (i > 0) {
      const prev = trkpts[trkpts.length - 1];
      distFromStart += haversineM(prev.lat, prev.lng, lat, lng);

      if (ele != null && prev.ele != null) {
        const delta = ele - prev.ele;
        if (delta > 0) elevationGainM += delta;
        else elevationLossM += Math.abs(delta);
      }
    }

    trkpts.push({ lat, lng, ele, time: timeText, distFromStart });
  }

  if (trkpts.length < 2) {
    throw new Error("GPX file has fewer than 2 valid coordinates.");
  }

  const totalDistanceM = trkpts[trkpts.length - 1].distFromStart;

  // Compute duration from timestamps if available
  let durationSec: number | null = null;
  const firstTime = trkpts.find((p) => p.time != null)?.time;
  const lastTime = [...trkpts].reverse().find((p) => p.time != null)?.time;
  if (firstTime && lastTime) {
    const diff = (new Date(lastTime).getTime() - new Date(firstTime).getTime()) / 1000;
    if (diff > 0) durationSec = diff;
  }

  return { name, trkpts, totalDistanceM, elevationGainM, elevationLossM, durationSec };
}
